import type {
  CreateHelpdeskRequestInput,
  CreateHelpdeskRequestResponse,
  HelpdeskCatalogFormResponse,
  HelpdeskCatalogResponse,
  HelpdeskCommentPage,
  HelpdeskDepartmentForms,
  HelpdeskLinearSharingUserSearchResponse,
  HelpdeskPeopleSearchResponse,
  HelpdeskPortalSharingDirectoryKind,
  HelpdeskPortalSharingDirectoryResponse,
  HelpdeskReadCursor,
  HelpdeskRequestActivityResponse,
  HelpdeskRequestBucket,
  HelpdeskRequestCancellation,
  HelpdeskRequestDetail,
  HelpdeskRequestPage,
  HelpdeskRequestRelationshipFilter,
  HelpdeskRequestSharing,
  HelpdeskUnreadCount,
  HelpdeskWebhookDeliverySummary,
  PostHelpdeskCommentInput,
  PostHelpdeskCommentResponse,
  PutHelpdeskPortalSharingInput,
  RetryHelpdeskRequestResponse,
} from "@repo/shared-types/helpdesk";
import { HELPDESK_ATTACHMENT_COMMENT_PART, HELPDESK_ATTACHMENT_FORM_PART_PREFIX } from "@repo/shared-types/helpdesk";
import type { HelpdeskPluginCatalogPage, HelpdeskPluginCatalogQuery } from "@repo/shared-types/helpdesk-plugins";
import { req } from "../../shared/web/client.ts";
import { multipartConversationRequest } from "../../shared/web/linear-conversation/client.ts";
import { fetchPluginCatalog } from "./plugin-catalog-client.ts";
import { confirmedSubmission } from "./submission-errors.ts";

export type HelpdeskFormAttachmentSubmission = { fieldId: string; file: File };

const pluginCatalogQueryParams = (query: HelpdeskPluginCatalogQuery): string =>
  new URLSearchParams({
    q: query.query,
    limit: String(query.limit),
    ...(query.cursor ? { cursor: query.cursor } : {}),
    ...(query.part === undefined ? {} : { part: query.part }),
    ...(query.selections === undefined ? {} : { selections: JSON.stringify(query.selections) }),
  }).toString();

function catalogPath(path: string, tenantId?: string): string {
  return tenantId === undefined ? path : `${path}?${new URLSearchParams({ tenantId })}`;
}

export const helpdeskRequesterApi = {
  catalog: (tenantId?: string): Promise<HelpdeskCatalogResponse> => req(catalogPath("/api/helpdesk/catalog", tenantId)),
  department: (departmentId: string, tenantId?: string): Promise<HelpdeskDepartmentForms> => req(catalogPath(`/api/helpdesk/catalog/categories/${departmentId}`, tenantId)),
  form: (versionId: string, tenantId?: string): Promise<HelpdeskCatalogFormResponse> => req(catalogPath(`/api/helpdesk/catalog/forms/${versionId}`, tenantId)),
  people: (versionId: string, fieldId: string, q: string): Promise<HelpdeskPeopleSearchResponse> =>
    req(`/api/helpdesk/catalog/forms/${versionId}/people?${new URLSearchParams({ fieldId, q }).toString()}`),
  pluginCatalog: (versionId: string, fieldId: string, query: HelpdeskPluginCatalogQuery): Promise<HelpdeskPluginCatalogPage> =>
    fetchPluginCatalog(`/api/helpdesk/catalog/forms/${versionId}/plugins/${fieldId}/catalog?${pluginCatalogQueryParams(query)}`),
  submit: (
    input: CreateHelpdeskRequestInput,
    attachments: readonly HelpdeskFormAttachmentSubmission[] = [],
    onProgress: (progress: number) => void = () => {},
  ): Promise<CreateHelpdeskRequestResponse> =>
    (attachments.length === 0
      ? req("/api/helpdesk/requests", { method: "POST", body: JSON.stringify(input) })
      : multipartConversationRequest(
          "/api/helpdesk/requests",
          input,
          attachments.map(({ fieldId, file }) => ({ fieldName: `${HELPDESK_ATTACHMENT_FORM_PART_PREFIX}${fieldId}`, file })),
          onProgress,
        )
    ).then((response) => confirmedSubmission(response, "/api/helpdesk/requests")),
  requests: (bucket: HelpdeskRequestBucket, relationship: HelpdeskRequestRelationshipFilter, cursor: string | null): Promise<HelpdeskRequestPage> =>
    req(`/api/helpdesk/requests?${new URLSearchParams({ bucket, relationship, ...(cursor ? { cursor } : {}) }).toString()}`),
  unreadCount: (bucket?: HelpdeskRequestBucket, relationship?: HelpdeskRequestRelationshipFilter): Promise<HelpdeskUnreadCount> =>
    req(`/api/helpdesk/requests/unread-count${bucket || relationship ? `?${new URLSearchParams({ ...(bucket ? { bucket } : {}), ...(relationship ? { relationship } : {}) })}` : ""}`),
  cancel: (requestId: string): Promise<HelpdeskRequestCancellation> => req(`/api/helpdesk/requests/${encodeURIComponent(requestId)}/cancel`, { method: "POST" }),
  activity: (requestIds: readonly string[], refresh: "stale" | "force" | "none" = "stale"): Promise<HelpdeskRequestActivityResponse> =>
    req(`/api/helpdesk/requests/activity?${new URLSearchParams({ requestIds: requestIds.join(","), refresh })}`),
  markAttentionRead: (requestId: string, targetId: string, episodeId: string): Promise<void> =>
    req(`/api/helpdesk/requests/${encodeURIComponent(requestId)}/targets/${encodeURIComponent(targetId)}/attention/read`, { method: "PUT", body: JSON.stringify({ episodeId }) }),
  markRead: (requestId: string, targetId: string, readCursor: HelpdeskReadCursor): Promise<void> =>
    req(`/api/helpdesk/requests/${requestId}/targets/${targetId}/read`, { method: "PUT", body: JSON.stringify({ readCursor }) }),
  request: (requestId: string): Promise<HelpdeskRequestDetail> => req(`/api/helpdesk/requests/${requestId}`),
  sharing: (requestId: string): Promise<HelpdeskRequestSharing> => req(`/api/helpdesk/requests/${requestId}/sharing`),
  portalSharingDirectory: (requestId: string, q: string, kind: HelpdeskPortalSharingDirectoryKind = "all"): Promise<HelpdeskPortalSharingDirectoryResponse> =>
    req(`/api/helpdesk/requests/${requestId}/sharing/portal-directory?${new URLSearchParams({ q, kind }).toString()}`),
  updatePortalSharing: (requestId: string, input: PutHelpdeskPortalSharingInput): Promise<HelpdeskRequestSharing> =>
    req(`/api/helpdesk/requests/${requestId}/sharing/portal`, { method: "PUT", body: JSON.stringify(input) }),
  linearSharingUsers: (versionId: string, q: string): Promise<HelpdeskLinearSharingUserSearchResponse> =>
    req(`/api/helpdesk/catalog/forms/${versionId}/linear-sharing-users?${new URLSearchParams({ q }).toString()}`),
  comments: (requestId: string, targetId: string, cursor: string | null): Promise<HelpdeskCommentPage> =>
    req(`/api/helpdesk/requests/${requestId}/targets/${targetId}/comments${cursor ? `?${new URLSearchParams({ cursor }).toString()}` : ""}`),
  comment: (
    requestId: string,
    targetId: string,
    input: PostHelpdeskCommentInput,
    attachments: readonly File[] = [],
    onProgress: (progress: number) => void = () => {},
  ): Promise<PostHelpdeskCommentResponse> =>
    attachments.length === 0
      ? req(`/api/helpdesk/requests/${requestId}/targets/${targetId}/comments`, { method: "POST", body: JSON.stringify(input) })
      : multipartConversationRequest(
          `/api/helpdesk/requests/${requestId}/targets/${targetId}/comments`,
          input,
          attachments.map((file) => ({ fieldName: HELPDESK_ATTACHMENT_COMMENT_PART, file })),
          onProgress,
        ),
  retry: (requestId: string, targetId: string): Promise<RetryHelpdeskRequestResponse> => req(`/api/helpdesk/requests/${requestId}/targets/${targetId}/retry`, { method: "POST" }),
};

export const helpdeskAutomationApi = {
  deliveries: (targetId: string): Promise<{ webhookDeliveries: HelpdeskWebhookDeliverySummary[] }> => req(`/api/helpdesk/targets/${encodeURIComponent(targetId)}/webhook-deliveries`),
  retrigger: (targetId: string, deliveryId: string, expectedGeneration: number): Promise<{ deliveryId: string; status: "queued" }> =>
    req(`/api/helpdesk/targets/${encodeURIComponent(targetId)}/webhook-deliveries/${encodeURIComponent(deliveryId)}/retrigger`, { method: "POST", body: JSON.stringify({ expectedGeneration }) }),
};
